import download from 'downloadjs';

import { Dispatch } from 'react';
import { AxiosError } from 'axios';
import {
  downloadMandatePreviewWithId,
  downloadMandateWithId,
  getFunds,
  getIdCardSignatureStatus,
  getMobileIdSignatureChallengeCode,
  getMobileIdSignatureStatus,
  getSmartIdSignatureChallengeCode,
  getSmartIdSignatureStatus,
  getSourceFunds,
  persistIdCardSignature,
  saveMandateWithAuthentication,
  startIdCardSignature,
} from '../common/api';
import { getIdCardSigningCertificate, signHashWithIdCard } from '../common/signing/signWithIdCard';
import { SigningCancelledByUser } from '../common/signing/signingCancelledByUser';
import { toSigningErrorResponse } from '../common/signing/signingErrorResponse';
import {
  CHANGE_AGREEMENT_TO_TERMS,
  GET_SOURCE_FUNDS_ERROR,
  GET_SOURCE_FUNDS_START,
  GET_SOURCE_FUNDS_SUCCESS,
  GET_TARGET_FUNDS_ERROR,
  GET_TARGET_FUNDS_START,
  GET_TARGET_FUNDS_SUCCESS,
  NO_SIGN_MANDATE_ERROR,
  SELECT_EXCHANGE_SOURCES,
  SELECT_TARGET_FUND,
  SET_CANCELLATION_MANDATE_ID,
  SIGN_MANDATE_ERROR,
  SIGN_MANDATE_ID_CARD_SIGN_HASH_SUCCESS,
  SIGN_MANDATE_ID_CARD_START,
  SIGN_MANDATE_ID_CARD_START_SUCCESS,
  SIGN_MANDATE_IN_PROGRESS,
  SIGN_MANDATE_INVALID_ERROR,
  SIGN_MANDATE_MOBILE_ID_CANCEL,
  SIGN_MANDATE_MOBILE_ID_START,
  SIGN_MANDATE_MOBILE_ID_START_SUCCESS,
  SIGN_MANDATE_SMART_ID_START,
  SIGN_MANDATE_SMART_ID_START_SUCCESS,
  SIGN_MANDATE_START_ERROR,
  SIGN_MANDATE_SUCCESS,
} from './constants';
import { actions as amlActions } from '../aml';
import { getAuthentication } from '../common/authenticationManager';
import { RootState } from '../account/ComparisonCalculator/types';
import { Mandate, SignatureStatus } from '../common/apiModels';
import { SourceSelection } from './types';

const POLL_DELAY = 1000;

const SIGNATURE_DONE_STATUS = 'SIGNATURE';
const SIGNING_IN_PROGRESS_STATUS = 'OUTSTANDING_TRANSACTION';

let timeout: number;

export function getAllSourceFunds() {
  return (dispatch: Dispatch<unknown>) => {
    dispatch({ type: GET_SOURCE_FUNDS_START });

    return getSourceFunds()
      .then((sourceFunds) => {
        dispatch({ type: GET_SOURCE_FUNDS_SUCCESS, sourceFunds });
      })
      .catch((error) => dispatch({ type: GET_SOURCE_FUNDS_ERROR, error }));
  };
}

export function selectExchangeSources(
  sourceSelection: SourceSelection[],
  sourceSelectionExact = false,
  targetFundIsin?: string,
) {
  return {
    type: SELECT_EXCHANGE_SOURCES,
    sourceSelection,
    sourceSelectionExact,
    targetFundIsin,
  };
}

export function changeAgreementToTerms(agreement: boolean) {
  return { type: CHANGE_AGREEMENT_TO_TERMS, agreement };
}

export function downloadMandate() {
  return (_: unknown, getState: () => RootState) => {
    const mandateId = getState().exchange.signedMandateId;
    if (mandateId && getAuthentication().isAuthenticated()) {
      return downloadMandateWithId(mandateId.toString()).then((file) =>
        download(file, 'Tuleva_avaldus.bdoc', 'application/bdoc'),
      );
    }
    return Promise.resolve();
  };
}

export function getTargetFunds() {
  return (dispatch: Dispatch<unknown>) => {
    dispatch({ type: GET_TARGET_FUNDS_START });
    return getFunds()
      .then((targetFunds) => dispatch({ type: GET_TARGET_FUNDS_SUCCESS, targetFunds }))
      .catch((error) => dispatch({ type: GET_TARGET_FUNDS_ERROR, error }));
  };
}

export function selectFutureContributionsFund(targetFundIsin: string | null) {
  return { type: SELECT_TARGET_FUND, targetFundIsin };
}

function pollForMobileIdSignature(mandateId: number, pillar: 2 | 3) {
  return (dispatch: Dispatch<unknown>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      getMobileIdSignatureStatus({ entityId: mandateId.toString() })
        .then((status) => {
          if (status.statusCode === SIGNING_IN_PROGRESS_STATUS) {
            dispatch({
              type: SIGN_MANDATE_IN_PROGRESS,
              controlCode: status.challengeCode,
            });
            dispatch(pollForMobileIdSignature(mandateId, pillar));
          } else {
            dispatch({
              type: SIGN_MANDATE_SUCCESS,
              signedMandateId: mandateId,
              pillar,
            });
          }
        })
        .catch((error) =>
          dispatch({ type: SIGN_MANDATE_ERROR, error: toSigningErrorResponse(error, 'MANDATE') }),
        );
    }, POLL_DELAY);
  };
}

function pollForSmartIdSignature(mandateId: number, pillar: 2 | 3) {
  return (dispatch: Dispatch<unknown>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      getSmartIdSignatureStatus({ entityId: mandateId.toString() })
        .then((status) => {
          if (status.statusCode === SIGNING_IN_PROGRESS_STATUS) {
            dispatch({
              type: SIGN_MANDATE_IN_PROGRESS,
              controlCode: status.challengeCode,
            });
            dispatch(pollForSmartIdSignature(mandateId, pillar));
          } else {
            dispatch({
              type: SIGN_MANDATE_SUCCESS,
              signedMandateId: mandateId,
              pillar,
            });
          }
        })
        .catch((error) =>
          dispatch({ type: SIGN_MANDATE_ERROR, error: toSigningErrorResponse(error, 'MANDATE') }),
        );
    }, POLL_DELAY);
  };
}

// should be AxiosError though status
function handleSaveMandateError(dispatch: Dispatch<unknown>, error: unknown) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (error?.status === 400) {
    dispatch({ type: SIGN_MANDATE_INVALID_ERROR, error });
  } else {
    dispatch({ type: SIGN_MANDATE_START_ERROR, error });
  }
}

function handleSigningError(dispatch: Dispatch<unknown>, error: unknown) {
  handleSaveMandateError(dispatch, toSigningErrorResponse(error, 'MANDATE'));
}

export function previewMandate(mandate: Mandate, amlChecks?: unknown) {
  return (dispatch: Dispatch<unknown>) => {
    const amlChecksPromise = dispatch(
      amlActions.createAmlChecks(amlChecks),
    ) as unknown as Promise<unknown>;

    return amlChecksPromise
      .then(() => saveOrRetrieveExistingMandate(mandate))
      .then(({ id }: Mandate) => downloadMandatePreviewWithId(id.toString()))
      .then((file: Blob) => download(file, 'Tuleva_avaldus_eelvaade.zip', 'application/zip'))
      .catch((error: string) => {
        handleSaveMandateError(dispatch, error);
      });
  };
}

export function signMandateWithMobileId(mandate: Mandate) {
  return (dispatch: Dispatch<unknown>) => {
    dispatch({ type: SIGN_MANDATE_MOBILE_ID_START });
    let mandateId: number;
    let mandatePillar: 2 | 3;

    return saveOrRetrieveExistingMandate(mandate)
      .then(({ id, pillar }) => {
        mandateId = id;
        mandatePillar = pillar;
        return getMobileIdSignatureChallengeCode({ entityId: mandateId.toString() });
      })
      .then((controlCode) => {
        dispatch({ type: SIGN_MANDATE_MOBILE_ID_START_SUCCESS, controlCode });
        dispatch(pollForMobileIdSignature(mandateId, mandatePillar));
      })
      .catch((error) => {
        handleSigningError(dispatch, error);
      });
  };
}

export function signMandateWithSmartId(mandate: Mandate) {
  return (dispatch: Dispatch<unknown>) => {
    dispatch({ type: SIGN_MANDATE_SMART_ID_START });
    let mandateId: number;
    let mandatePillar: 2 | 3;
    return saveOrRetrieveExistingMandate(mandate)
      .then(({ id, pillar }) => {
        mandateId = id;
        mandatePillar = pillar;
        return getSmartIdSignatureChallengeCode({ entityId: mandateId.toString() });
      })
      .then((controlCode) => {
        dispatch({ type: SIGN_MANDATE_SMART_ID_START_SUCCESS, controlCode });
        dispatch(pollForSmartIdSignature(mandateId, mandatePillar));
      })
      .catch((error) => {
        handleSigningError(dispatch, error);
      });
  };
}

function handleIdCardSignatureStatus(
  dispatch: Dispatch<unknown>,
  statusCode: SignatureStatus,
  mandateId: number,
  pillar: 2 | 3,
) {
  if (statusCode === SIGNATURE_DONE_STATUS) {
    dispatch({ type: SIGN_MANDATE_SUCCESS, signedMandateId: mandateId, pillar });
  } else {
    dispatch(pollForIdCardSignature(mandateId, pillar));
  }
}

function pollForIdCardSignature(mandateId: number, pillar: 2 | 3) {
  return (dispatch: Dispatch<unknown>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(() => {
      getIdCardSignatureStatus({ entityId: mandateId.toString() })
        .then((statusCode) => handleIdCardSignatureStatus(dispatch, statusCode, mandateId, pillar))
        .catch((error) =>
          dispatch({ type: SIGN_MANDATE_ERROR, error: toSigningErrorResponse(error, 'MANDATE') }),
        );
    }, POLL_DELAY);
  };
}

function saveOrRetrieveExistingMandate(mandate: Mandate | string) {
  if (typeof mandate === 'object' && mandate.id) {
    return Promise.resolve(mandate);
  }
  return saveMandateWithAuthentication(mandate as string);
}

export function signMandateWithIdCard(mandate: Mandate) {
  return (dispatch: Dispatch<unknown>) => {
    dispatch({ type: SIGN_MANDATE_ID_CARD_START });
    let certificate: string;
    let supportedHashFunctions: string[];
    let mandateId: number;
    let mandatePillar: 2 | 3;

    return getIdCardSigningCertificate()
      .then((signingCertificate) => {
        certificate = signingCertificate.certificate;
        supportedHashFunctions = signingCertificate.supportedHashFunctions;
        return saveOrRetrieveExistingMandate(mandate);
      })
      .then(({ id, pillar }: Mandate) => {
        mandateId = id;
        mandatePillar = pillar;
        return startIdCardSignature({
          entityId: mandateId.toString(),
          certificate,
          supportedHashFunctions,
        });
      })
      .then((hashToSign) => {
        dispatch({ type: SIGN_MANDATE_ID_CARD_START_SUCCESS });
        return signHashWithIdCard(certificate, hashToSign);
      })
      .then((signature) => {
        dispatch({ type: SIGN_MANDATE_ID_CARD_SIGN_HASH_SUCCESS });
        return persistIdCardSignature({ entityId: mandateId.toString(), signature });
      })
      .then((statusCode) =>
        handleIdCardSignatureStatus(dispatch, statusCode, mandateId, mandatePillar),
      )
      .catch((error) => {
        if (error instanceof SigningCancelledByUser) {
          dispatch(cancelSigningMandate());
        } else {
          handleSigningError(dispatch, error);
        }
      });
  };
}

export function signMandate(mandate: Mandate, amlChecks?: unknown) {
  return (dispatch: Dispatch<unknown>) => {
    const { signingMethod } = getAuthentication();
    const amlChecksPromise = dispatch(
      amlActions.createAmlChecks(amlChecks),
    ) as unknown as Promise<unknown>;

    return amlChecksPromise
      .then(() => {
        if (signingMethod === 'MOBILE_ID') {
          return dispatch(signMandateWithMobileId(mandate));
        }
        if (signingMethod === 'SMART_ID') {
          return dispatch(signMandateWithSmartId(mandate));
        }
        if (signingMethod === 'ID_CARD') {
          return dispatch(signMandateWithIdCard(mandate));
        }
        throw new Error(`Invalid signing method: ${signingMethod}`);
      })
      .catch((error: AxiosError) => {
        handleSigningError(dispatch, error);
      });
  };
}

export function cancelSigningMandate() {
  if (timeout && process.env.NODE_ENV !== 'test') {
    clearTimeout(timeout);
  }
  return { type: SIGN_MANDATE_MOBILE_ID_CANCEL };
}

export function closeErrorMessages() {
  return { type: NO_SIGN_MANDATE_ERROR };
}

export function setCancellationMandateId(mandateId: number | null) {
  return { type: SET_CANCELLATION_MANDATE_ID, mandateId };
}
